import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { EventModel } from '@app/events/shared/event.model';
import { provideFlatpickrDefaults } from 'angularx-flatpickr';
import { Edit, Trash, Plus, Trash2, BadgeCheck, Star, Gift } from 'lucide-angular';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ProductService } from '@app/products/shared/product.service';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { SearchableSelectComponent } from '@app/partials/form/searchable-select/searchable-select.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { EventProductItemModel } from '@app/events/shared/event-product-item.model';
import { EventProductItemVariantSelectionRequest } from '@app/events/shared/event-product-item-variant-selection.model';
import { ProductModel } from '@app/products/shared/product.model';
import { RouteParams } from '@app/shared/abstract/route-params';
import { ProductVariantModel } from '@app/products/shared/product-variant.model';
import { ProductVariantOptionModel } from '@app/products/shared/product-variant-option.model';
import { NgFor, KeyValuePipe, NgClass } from '@angular/common';
import { Observable } from 'rxjs';
import { ProductItemPricingComponent } from '@app/events/shared/product-item-pricing.component';
import { VariantPricingLabelComponent } from '@app/events/shared/variant-pricing-label.component';
import { LucideAngularModule } from 'lucide-angular';
import { DateUtil } from '@app/shared/utils/date.util';

@Component({
  selector: 'app-event-product-item-form',
  templateUrl: './product-item-form.component.html',
  styleUrls: [],
  providers: [provideFlatpickrDefaults()],
  imports: [SelectComponent, SearchableSelectComponent, TextInputComponent, ButtonComponent, ProductItemPricingComponent, VariantPricingLabelComponent, LucideAngularModule, KeyValuePipe, NgClass],
  standalone: true,
})
export class ProductItemFormComponent implements OnInit {
  @Input() public event!: EventModel;
  @Input() filterPreset?: { minSpend: boolean; qualifying: boolean; offer: boolean };
  @Input() filtersLocked = false;
  @Input() submitLabel = 'Add Product';
  @Input() showTimelineSelector = false;
  @Input() timelineItemChoices: { value: number | null; label: string }[] = [];

  @Output() itemCreated = new EventEmitter<EventProductItemModel>();

  @ViewChild(ProductItemPricingComponent) pricingComponent?: ProductItemPricingComponent;

  protected readonly TrashIcon = Trash;
  protected readonly EditIcon = Edit;
  protected readonly Plus = Plus;
  protected readonly Trash2 = Trash2;
  protected readonly BadgeCheck = BadgeCheck;
  protected readonly StarIcon = Star;
  protected readonly GiftIcon = Gift;

  protected initialProductChoices?: { value: number | null; label: string }[];
  protected selectedProduct: ProductModel | null = null;
  protected productSearchFunction: (term: string, page?: number) => Observable<{ options: { value: any; label: string; meta?: any }[], hasMore: boolean }>;
  protected productFilters: { minSpend: boolean; qualifying: boolean; offer: boolean } = {
    minSpend: false,
    qualifying: false,
    offer: false,
  };
  protected serverError: string | null = null;

  protected form: FormGroup<{
    amount: FormControl<null>;
    date_purchased: FormControl<string | null>;
    is_paid: FormControl<false | true | null>;
    product_id: FormControl<number | null>;
    quantity: FormControl<null>;
    event_timeline_item_id: FormControl<number | null>;
    variant_selections: FormArray<FormGroup<{
      variant_id: FormControl<number>;
      variant_option_id: FormControl<number | null>;
      quantity: FormControl<number>;
      notes: FormControl<string | null>;
    }>>;
  }>;

  disabledControl: FormControl<any> = new FormControl({ value: '', disabled: true });

  constructor(private productService: ProductService) {

    // Set up the search function for the searchable select
    this.productSearchFunction = (term: string, page?: number) => {
      return this.productService.searchProducts(term, page || 1, 50, this.productFilters, this.availabilityDateString || undefined);
    };
    this.form = new FormGroup({
      quantity: new FormControl(null),
      amount: new FormControl(null),
      date_purchased: new FormControl(new Date().toISOString().slice(0, 19).replace('T', ' ')),
      // VG-281: TEMPORARILY CHANGED - Paid status feature (2025-09-30): Changed default from true to false
      // To revert: Change back to `new FormControl(true)` and also update line 203
      is_paid: new FormControl(false),
      product_id: new FormControl<number | null>(null, [Validators.required]),
      event_timeline_item_id: new FormControl<number | null>(null),
      variant_selections: new FormArray<FormGroup<{
        variant_id: FormControl<number>;
        variant_option_id: FormControl<number | null>;
        quantity: FormControl<number>;
        notes: FormControl<string | null>;
      }>>([]),
    });

    this.form.controls.product_id.valueChanges.subscribe((value) => {
      // Clear existing variant selections when product changes
      this.form.controls.variant_selections.clear();

      // Always fetch the full product with variants when selected (no caching)
      if (value) {
        const eventDate = this.availabilityDateString;
        const req$ = eventDate
          ? this.productService.getByIdForDate(value, eventDate)
          : this.productService.getById(value);

        req$.subscribe(response => {
          this.selectedProduct = response.data;
          this.updateProductDependentFields();
        });
      } else {
        this.selectedProduct = null;
      }
    });
  }

  ngOnInit(): void {
    if (this.filterPreset) {
      this.productFilters = {
        ...this.productFilters,
        ...this.filterPreset,
      };
    }
    // Load initial popular/recent products for the dropdown using lightweight endpoint
    // Full product data with variants will be fetched when user selects a product
    this.refreshProductOptions();
  }

  private updateProductDependentFields(): void {
    // VG-396: Clear existing variant selections
    this.form.controls.variant_selections.clear();

    // Auto-create selections for 0-option variants (accommodation case)
    // For these variants, show only notes input with quantity=1 in background
    if (this.selectedProduct?.variants) {
      this.selectedProduct.variants.forEach(variant => {
        if (!variant.options || variant.options.length === 0) {
          // Create form group with quantity=1 and no option
          const variantGroup = new FormGroup({
            variant_id: new FormControl<number>(variant.id!, { nonNullable: true }),
            variant_option_id: new FormControl<number | null>(null),
            quantity: new FormControl<number>(1, { nonNullable: true }),
            notes: new FormControl<string | null>(null),
          });
          this.form.controls.variant_selections.push(variantGroup);
        }
      });
    }

    // VG-395: For variant-based products, quantity is not used (each variant has its own quantity)
    if (this.selectedProduct?.is_variant_based) {
      this.form.controls.quantity.clearValidators();
      this.form.controls.amount.clearValidators();
    } else {
      // Update validators based on whether the product has a price
      if (this.selectedProduct?.price) {
        this.form.controls.quantity.setValidators([Validators.required]);
        this.form.controls.amount.clearValidators();
      } else {
        this.form.controls.amount.setValidators([Validators.required]);
        this.form.controls.quantity.clearValidators();
      }
    }

    // Recalculate validation status
    this.form.controls.quantity.updateValueAndValidity();
    this.form.controls.amount.updateValueAndValidity();
  }

  protected toggleFilter(filter: 'minSpend' | 'qualifying' | 'offer'): void {
    this.productFilters = {
      ...this.productFilters,
      [filter]: !this.productFilters[filter],
    };
    this.refreshProductOptions();
  }

  protected stopFilterClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  private refreshProductOptions(): void {
    this.productService
      .searchProducts('', 1, 50, this.productFilters, this.availabilityDateString || undefined)
      .subscribe((res) => {
        this.initialProductChoices = res.options;
      });
  }

  protected get availabilityDateString(): string | null {
    return DateUtil.parseDateToString(this.event?.start_date);
  }

  /**
   * Programmatically select a product in the form (used by offer slider quick-pick list)
   */
  selectProduct(productId: number): void {
    if (!productId) {
      return;
    }
    this.form.controls.product_id.setValue(productId);
  }

  // VG-396: Add a new variant option selection for a specific variant
  addVariantOption(variant: ProductVariantModel): void {
    // VG-395: variant_option_id is optional (Standard = no option = £0)
    const optionValidators: any[] = [];

    // VG-381: Create form controls for variants, even those without options
    // Variants without options (like accommodation rooms) still need quantity and notes
    const variantOptionControl = new FormControl<number | null>(null, optionValidators);

    // VG-381: Disable variant_option_id control if variant has no options
    if (!variant.options || variant.options.length === 0) {
      variantOptionControl.disable();
    }

    const variantGroup = new FormGroup({
      variant_id: new FormControl<number>(variant.id!, { nonNullable: true }),
      variant_option_id: variantOptionControl,
      // VG-396: Default quantity to 1 for new selections (user can adjust)
      quantity: new FormControl<number>(1, { nonNullable: true, validators: [Validators.min(0)] }),
      notes: new FormControl<string | null>(null),
    });

    // VG-395: For variant-based products, when a variant option is selected, set quantity to 1
  if (this.selectedProduct?.is_variant_based) {
    variantGroup.controls.variant_option_id.valueChanges.subscribe(value => {
      if (value !== null && variantGroup.controls.quantity.value === 0) {
        variantGroup.controls.quantity.setValue(1);
      }
      this.applyMaxValidator(variantGroup);
      this.clearServerErrorOnControl(variantGroup);
    });
  }

  // Apply max validator on init
  this.applyMaxValidator(variantGroup);
  variantGroup.controls.quantity.valueChanges.subscribe(() => this.clearServerErrorOnControl(variantGroup));

  this.form.controls.variant_selections.push(variantGroup);
  }

  // VG-396: Remove a variant option selection by index
  removeVariantOption(index: number): void {
    this.form.controls.variant_selections.removeAt(index);
  }

  applyServerError(message: string): void {
    this.serverError = message;
    this.form.controls.variant_selections.controls.forEach((ctrl) => {
      const qty = ctrl.get('quantity') as FormControl<number>;
      if (qty) {
        const errs = { ...(qty.errors || {}) };
        errs['server'] = message;
        qty.setErrors(errs);
        qty.markAsTouched();
      }
    });
  }

  clearServerErrorOnControl(ctrl: FormGroup): void {
    const qty = ctrl.get('quantity') as FormControl<number>;
    if (!qty) return;
    if (qty.errors && qty.errors['server']) {
      const { server, ...rest } = qty.errors;
      qty.setErrors(Object.keys(rest).length ? rest : null);
    }
    this.serverError = null;
  }

  private applyMaxValidator(ctrl: FormGroup): void {
    const optionId = ctrl.get('variant_option_id')?.value;
    const variantId = ctrl.get('variant_id')?.value;
    const quantityCtrl = ctrl.get('quantity') as FormControl<number>;
    if (!quantityCtrl) return;
    const validators = [Validators.min(0)];

    if (optionId && variantId) {
      const variant = this.getVariantById(variantId);
      const option = variant?.options?.find((opt) => opt.id === optionId);
      const max = (option as any)?.['max_quantity'];
      if (max !== undefined && max !== null) {
        validators.push(Validators.max(max));
        const current = quantityCtrl.value ?? 0;
        if (current > max) {
          quantityCtrl.setValue(max, { emitEvent: false });
        }
      }
    }

    quantityCtrl.setValidators(validators);
    quantityCtrl.updateValueAndValidity({ emitEvent: false });
  }

  // VG-396: Get variant by ID helper
  getVariantById(variantId: number): ProductVariantModel | undefined {
    return this.selectedProduct?.variants?.find(v => v.id === variantId);
  }

  // VG-396: Group variant selections by variant for template display
  getVariantSelectionsByVariant(): Map<number, { variant: ProductVariantModel, indices: number[] }> {
    const groupedMap = new Map<number, { variant: ProductVariantModel, indices: number[] }>();

    // Group existing selections by variant_id
    this.form.controls.variant_selections.controls.forEach((control, index) => {
      const variantId = control.value.variant_id!;
      const variant = this.getVariantById(variantId);

      if (variant) {
        if (!groupedMap.has(variantId)) {
          groupedMap.set(variantId, { variant, indices: [] });
        }
        groupedMap.get(variantId)!.indices.push(index);
      }
    });

    // Add variants that have no selections yet (so we can show "Add" button)
    this.selectedProduct?.variants?.forEach(variant => {
      if (!groupedMap.has(variant.id!)) {
        groupedMap.set(variant.id!, { variant, indices: [] });
      }
    });

    return groupedMap;
  }

  // Simplified helper method to determine if we should show the pricing section
  // Always show pricing section when product is selected to display base price and calculated price
  get shouldShowPricingSection(): boolean {
    return this.selectedProduct !== null;
  }

  get totalVariantQuantity(): number {
    return this.form.controls.variant_selections.controls.reduce((sum, ctrl) => {
      const rawQty = ctrl.get('quantity')?.value;
      const qty = typeof rawQty === 'number' ? rawQty : Number(rawQty);
      return sum + (isNaN(qty) ? 0 : qty);
    }, 0);
  }

  getVariantOptionsChoices(variantId: number): { value: number | null; label: string }[] {
    const variant = this.selectedProduct?.variants?.find(v => v.id === variantId);

    // VG-381: If variant has no options, return just the standard option
    if (!variant?.options || variant.options.length === 0) {
      return [{ value: null, label: 'Standard (no upgrade)' }];
    }

    const choices: { value: number | null; label: string }[] = variant.options.map(option => {
      const price = option.price ?? 0;
      const category = option.category || 'Full Price';
      const priceLabel = `£${price}`;
      const categoryLabel = category === 'Complimentary' ? 'Complimentary' : category === 'Half Price' ? 'Half Price' : 'Full Price';
      const label = `${option.name} (${categoryLabel} – ${priceLabel})`;

      return {
        value: option.id!,
        label,
      };
    });

    if (this.shouldIncludeStandardVariantChoice()) {
      choices.unshift({ value: null, label: 'Standard (no upgrade)' });
    }

    return choices;
  }

  private shouldIncludeStandardVariantChoice(): boolean {
    return this.selectedProduct?.category !== 'Complimentary Items';
  }

  getVariantOptionControl(variantControl: any): any {
    return variantControl.get('variant_option_id');
  }

  getQuantityControl(variantControl: any): FormControl<number> {
    return variantControl.get('quantity') as FormControl<number>;
  }

  getNotesControl(variantControl: any): any {
    return variantControl.get('notes');
  }

  getOptionInfo(selectionControl: FormGroup): string {
    const optionId = selectionControl.get('variant_option_id')?.value;
    const variantId = selectionControl.get('variant_id')?.value;
    if (!optionId || !variantId) return '';
    const variant = this.getVariantById(variantId);
    const option = variant?.options?.find((opt) => opt.id === optionId);
    return option?.info_message || '';
  }

  get hasVariants(): boolean {
    return !!(this.selectedProduct?.variants && this.selectedProduct.variants.length > 0);
  }

  get isVariantBased(): boolean {
    return this.selectedProduct?.is_variant_based === true;
  }

  // Helper to check if variant has no options (accommodation case)
  hasNoOptions(variant: ProductVariantModel): boolean {
    return !variant.options || variant.options.length === 0;
  }

  getSelectionMax(selectionControl: FormGroup): number | undefined {
    const optionId = selectionControl.get('variant_option_id')?.value;
    const variantId = selectionControl.get('variant_id')?.value;
    if (!optionId || !variantId) return undefined;
    const variant = this.getVariantById(variantId);
    const option = variant?.options?.find((opt) => opt.id === optionId);
    const max = (option as any)?.['max_quantity'];
    if (max === undefined || max === null) return undefined;
    return max;
  }

  getSelectionCapHelp(selectionControl: FormGroup): string {
    const max = this.getSelectionMax(selectionControl);
    if (!max) return '';
    const variantId = selectionControl.get('variant_id')?.value;
    const optionId = selectionControl.get('variant_option_id')?.value;
    const variant = variantId ? this.getVariantById(variantId) : undefined;
    const option = variant?.options?.find((opt) => opt.id === optionId);
    const label = option?.name || 'this option';
    return `Max ${max} for ${label}`;
  }

  save() {
    if (!this.form) {
      return;
    }
    if (this.form.invalid) {
      return;
    }

    // VG-403: Build complete EventProductItemModel with nested objects for immediate display in table
    // The table template expects full product/variant objects, not just IDs

    // Map variant selections to include full variant and variant_option objects
    const variantSelectionsWithObjects = this.form.value.variant_selections
      ?.filter(selection => {
        // Find the variant to check if it has options
        const variant = this.selectedProduct?.variants?.find(v => v.id === selection.variant_id);
        const hasNoOptions = !variant?.options || variant.options.length === 0;

        // For 0-option variants (accommodation): always include with quantity > 0
        if (hasNoOptions) {
          return selection.quantity! > 0;
        }

        // For variants with options: filter out selections with no option selected
        if (selection.variant_option_id === null) {
          return false;
        }

        // For variant-based products: include all selections with options
        if (this.selectedProduct?.is_variant_based) {
          return true;
        }
        // For regular products with variants: only include selections with quantity > 0
        return selection.quantity! > 0;
      })
      .map((selection, index) => {
        // Find the full variant and option objects from selectedProduct
        const variant = this.selectedProduct?.variants?.find(v => v.id === selection.variant_id);
        const variantOption = variant?.options?.find(o => o.id === selection.variant_option_id);

        return {
          // VG-403: Generate temporary negative ID for tracking in @for loops (will be replaced after save)
          id: -(Date.now() + index),
          variant_id: selection.variant_id!,
          variant_option_id: selection.variant_option_id,
          quantity: selection.quantity!,
          notes: selection.notes,
          // Include full objects for table display
          variant: variant,
          variant_option: variantOption,
        };
      }) || [];

    // Create complete EventProductItemModel structure
    const productItemModel: any = {
      // Generate temporary negative ID for tracking (will be replaced by real ID after save)
      id: -Date.now(),
      quantity: this.form.value.quantity ?? null,
      amount: this.form.value.amount ?? null,
      date_purchased: this.form.value.date_purchased === '' ? null : this.form.value.date_purchased,
      is_paid: this.form.value.is_paid ?? false,
      product_id: this.form.value.product_id,
      event_id: this.event.id,
      event_timeline_item_id: this.form.value.event_timeline_item_id ?? null,
      // Include calculated price from pricing component for immediate display
      calculated_price: this.pricingComponent?.calculatedPrice ?? null,
      // Include full product object for table display
      product: this.selectedProduct,
      // Include variant selections with full nested objects
      variant_selections: variantSelectionsWithObjects.length > 0 ? variantSelectionsWithObjects : [],
    };

    this.itemCreated.emit(productItemModel as EventProductItemModel);

    this.form.reset();
    // VG-281: TEMPORARILY CHANGED - Paid status feature (2025-09-30): Changed from true to false
    // To revert: Change back to `setValue(true)` and also update line 72
    this.form.controls.is_paid.setValue(false);
    this.form.controls.event_timeline_item_id.setValue(null);
    this.form.controls.date_purchased.setValue(new Date().toISOString().slice(0, 19).replace('T', ' '));
    this.form.markAsPristine();
  }
}
